import { computed, inject, Injectable, OnDestroy, signal, Signal, untracked } from '@angular/core';
import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  SortModelItem,
} from 'ag-grid-community';
import { BehaviorSubject, distinctUntilChanged, isObservable, Subject } from 'rxjs';
import { DATA_GRID_DATA_SOURCES, DATA_GRID_FILTER_SERVICE, DATA_GRID_READER_SERVICE } from './injection-tokens';
import {
  DataSourceRequestStatus,
  GridDataSources,
  GridDataSourceSortFilter,
  GridFilterService,
  GridSort,
  ReaderObject,
  ReaderService,
} from './types';

@Injectable()
export class GridDataSourceServerSideService<DM> implements IServerSideDatasource, OnDestroy {
  private readonly dataSource = inject<GridDataSources<DM, IServerSideGetRowsRequest>>(DATA_GRID_DATA_SOURCES);
  private readonly filterService = inject<GridFilterService<DM>>(DATA_GRID_FILTER_SERVICE);
  private readonly readerService = inject<ReaderService<DM>>(DATA_GRID_READER_SERVICE);

  private readonly requestStatusSubject = new Subject<DataSourceRequestStatus>();
  private readonly totalSubject = new BehaviorSubject<number | null>(null);
  private readonly sort: GridDataSourceSortFilter<GridSort> = {
    hasChange: false,
    value: [],
    originalValue: [],
  };

  // readonly dataChange$: Observable<ReaderObject<DM>[]> = interval(3000).pipe(
  //   filter(() => this.readerService.shouldRead()),
  //   switchMap(() => this.dataSource.readByIds(this.readerService.ids())),
  //   map((rows) => this.readerService.filterChanged(rows as ReaderObject<DM>[])),
  //   filter((ar) => ar.length > 0),
  //   share()
  // );

  readonly dataChange$: Signal<ReaderObject<DM>[]> = computed(() => {
    if (this.readerService.shouldRead() && this.dataSource.readDataListener()) {
      const rows = this.dataSource.readByIds(this.readerService.ids());
      const data = this.readerService.filterChanged(rows() as ReaderObject<DM>[]);
      if (data.length > 0) return data;
    }
    return [];
  });
  // (this.dataSource.readDataListener() as Observable<any>).pipe(
  //   filter(() => this.readerService.shouldRead()),
  //   switchMap(() => this.dataSource.readByIds(this.readerService.ids())),
  //   map((rows) => this.readerService.filterChanged(rows as ReaderObject<DM>[])),
  //   filter((ar) => ar.length > 0),
  //   share()
  // );

  readonly requestStatus$ = this.requestStatusSubject.asObservable();
  readonly requestStatusSignal = signal<DataSourceRequestStatus>('initial'); //toSignal(this.requestStatusSubject.asObservable());

  readonly total$ = this.totalSubject.asObservable().pipe(distinctUntilChanged());

  rowCount?: number | undefined;

  /**
   * The response is tied to the request completing, not to the cache as it stands when the request
   * is made.
   *
   * This used to write the params into a signal that an effect read alongside `readSignal()`. The
   * effect ran as soon as the params were set, which is before `sendReadRequest` has awaited
   * anything, so an initial Registry or Events load answered the very first block from the empty
   * cache with `rowCount: 0`. The store then filled in, `readSignal` changed, the same effect ran
   * again and called `success` a second time on a param whose block AG Grid had already completed.
   */
  getRows(getParams: IServerSideGetRowsParams<DM>): void {
    this.requestStatusSubject.next('before');
    this.requestStatusSignal.set('before');
    try {
      this.readerService.resetReader();
      const pending = this.dataSource.sendReadRequest(getParams.request);
      if (!isObservable(pending)) {
        // Nothing to wait on, so this is the old behaviour and carries the old defect. Said out
        // loud rather than served quietly: rows from before the request are not this block's rows.
        console.warn(
          'GridDataSourceServerSide: the data source returned no completion signal, answering from the current cache.',
        );
        this.respondWithBlock(getParams);
        return;
      }

      let responded = false;
      pending.subscribe({
        complete: () => {
          if (!responded) {
            responded = true;
            this.respondWithBlock(getParams);
          }
        },
        error: (requestError: unknown) => {
          if (!responded) {
            responded = true;
            this.failRequest(getParams, requestError);
          }
        },
      });
    } catch (error) {
      this.failRequest(getParams, error);
    }
  }

  /**
   * Answers with the requested block only. The stores size their cache array to the collection's
   * total count and fill just the slice they fetched, so `readSignal().rows` is a sparse array as
   * long as the whole collection: handing that to `success` gave the grid rows from outside the
   * block plus holes, and a hole reaches `getRowId`, which dereferences `data.ref` and aborts the
   * render. `total` stays the full count, which is what `rowCount` is for.
   */
  private respondWithBlock(getParams: IServerSideGetRowsParams<DM>): void {
    const { rows, total } = untracked(() => this.dataSource.readSignal());
    const startRow = getParams.request.startRow ?? 0;
    const endRow = getParams.request.endRow ?? rows.length;
    const requestedBlock = rows.slice(startRow, endRow);
    // `filter` skips holes, which is exactly the point; the length comparison is what makes a
    // partially populated block visible instead of silently short.
    const blockRows = requestedBlock.filter((row) => row !== undefined && row !== null);
    if (blockRows.length !== requestedBlock.length) {
      console.warn(
        `GridDataSourceServerSide: block [${startRow}, ${endRow}) came back with ` +
          `${requestedBlock.length - blockRows.length} unpopulated row(s), which were dropped.`,
      );
    }

    getParams.success({ rowData: blockRows, rowCount: total });
    this.totalSubject.next(total);
    this.requestStatusSubject.next('success');
    this.requestStatusSignal.set('success');
    this.readerService.newDataLoaded(blockRows as ReaderObject<DM>[]);
  }

  private failRequest(getParams: IServerSideGetRowsParams<DM>, error: unknown): void {
    console.error('getRows ERROR:', error);
    this.requestStatusSignal.set('error');
    this.requestStatusSubject.next('error');
    getParams.fail();
  }

  destroy?(): void {
    this.resetReader();
  }

  ngOnDestroy(): void {
    this.requestStatusSubject.complete();
    this.totalSubject.complete();
  }

  resetReader(): void {
    this.readerService.resetReader();
  }

  addReader(rows: ReaderObject<DM>[]): void {
    this.readerService.newDataLoaded(rows);
  }

  private mapSort(sortModel: SortModelItem[]) {
    const gridSport: GridSort[] = sortModel.map(({ colId, sort }) => ({ field: colId, sort }));
    this.sort.hasChange = this.sort.originalValue !== gridSport;

    if (this.sort.hasChange) {
      this.sort.originalValue = gridSport;
      this.sort.value = gridSport;
    }
  }
}
